import React, { useState, useEffect, useMemo, memo } from "react";
import { fetchCategory } from "../../../domain/usecases/fetchCategoryProduct"; // Import the fetch function
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";
import { CategoryProduct } from "../../../domain/entities/CategoryProduct";
import { sendRegisterSudirman } from "../../../domain/usecases/fetchRegister";
import logoLM from "../../../assets/Logo/logo.png";
import TabsComponent from "../../components/selectProductComponents/TabComponents";
import { useNavigate } from 'react-router-dom';
import { ProductItem } from "../../../domain/entities/ProductItem";
import ModalRegister from "../../components/modal/ModalRegister";
import MapSudirman from "../../components/mapComponents/MapSudirman";
import CascadingApartementDropdown from "../../components/selectDropdownComponents/CascadingApartementDropdown";
import ListProductsRegisterSupark from "../../components/selectProductComponents/ListProductsRegisterSupark";
import PrivacyCheckboxComponents from "../../components/privacyPolicyComponents/PrivacyCheckboxComponents";

// Add interface for InputField props
interface InputFieldProps {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    type?: string;
    placeholder?: string;
    className?: string;
}

// Add interface for ImageUpload props
interface ImageUploadProps {
    label: string;
    onFileSelect: (file: File | null) => void;
    error?: string;
    acceptedFormats?: string;
    maxSize?: number; // in MB
    previewUrl?: string;
}

// Modified input field component to show error state
const InputField = memo(({ 
    label, 
    value, 
    onChange, 
    error, 
    type = "text", 
    placeholder,
    className = "" 
}: InputFieldProps) => {
    // Handle input change directly
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e);
    };

    return (
        <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 font-poppins">{label} *</label>
            <input
                type={type}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                className={`w-full h-10 px-4 border font-poppins rounded-lg mt-1 text-sm focus:ring focus:ring-indigo-300 
                    ${error ? 'border-red-500' : 'border-gray-300'} ${className}`}
            />
            {error && (
                <p className="mt-1 text-xs text-red-500 font-poppins">{error}</p>
            )}
        </div>
    );
});

// Image Upload Component
const ImageUpload = memo(({ 
    label, 
    onFileSelect, 
    error, 
    acceptedFormats = ".jpg,.jpeg,.png", 
    maxSize = 5,
    previewUrl 
}: ImageUploadProps) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = (file: File | null) => {
        if (!file) {
            onFileSelect(null);
            return;
        }

        // Validate file size
        if (file.size > maxSize * 1024 * 1024) {
            alert(`File size must be less than ${maxSize}MB`);
            return;
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            alert('Please upload a valid image file (JPG, JPEG, PNG)');
            return;
        }

        onFileSelect(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        handleFileChange(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0] || null;
        handleFileChange(file);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        onFileSelect(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 font-poppins">{label} *</label>
            
            <div
                className={`w-full border-2 border-dashed rounded-lg mt-1 p-4 text-center cursor-pointer transition-colors
                    ${isDragOver ? 'border-blue-400 bg-blue-50' : error ? 'border-red-500' : 'border-gray-300'}
                    hover:border-blue-400 hover:bg-blue-50`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedFormats}
                    onChange={handleInputChange}
                    className="hidden"
                />
                
                {previewUrl ? (
                    <div className="relative">
                        <img 
                            src={previewUrl} 
                            alt="Preview" 
                            className="max-h-32 mx-auto rounded-lg"
                        />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile();
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <div className="py-4">
                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-gray-600 font-poppins">
                            Drag and drop your file here, or <span className="text-blue-600 underline">click to browse</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1 font-poppins">
                            Supported formats: JPG, JPEG, PNG (Max {maxSize}MB)
                        </p>
                    </div>
                )}
            </div>
            
            {error && (
                <p className="mt-1 text-xs text-red-500 font-poppins">{error}</p>
            )}
        </div>
    );
});

const RegistrationSudirmanPages: React.FC = () => {
    const defaultCategory: CategoryProduct = {
        id: 1,
        slug: "broadband-internet",
        is_price: false,
        category: "Broadband Internet",
        short_description: "",
        long_description: "",
        background_image: "",
        banner_products: "",
        products: [],
        benefits: []
    };

    const [fullname, setFullname] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [categorySlug, setCategorySlug] = useState<string>("");
    const [isPrice, setIsPrice] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryProduct>(defaultCategory);
    const [productItems, setProductItems] = useState<ProductItem[]>([]);
    const [productId, setProductId] = useState<number>(0);
    const [towerId, setTowerId] = useState<string>("");
    const [productPrice, setProductPrice] = useState<number>(0);
    const [totalTransaction, setTotalTransaction] = useState<number>(0);
    const [isModalOpen, setModalOpen] = useState(false);
    const [categories, setCategories] = useState<CategoryProduct[]>([]);
    const [loading, setLoading] = useState(true); // State for loading
    const [error, setError] = useState<string | null>(null); // State for errors
    const navigate = useNavigate();
    const [userLocation, setUserLocation] = useState<[number, number]>();
    const [locationError, setLocationError] = useState<string>('');
    const defaultCenter: [number, number] = [-6.200000, 106.816666]; // Jakarta coordinates
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

    // Add new state for image upload
    const [idCardFile, setIdCardFile] = useState<File | null>(null);
    const [idCardPreview, setIdCardPreview] = useState<string>("");

    const handleButtonClick = async () => {
        if (!idCardFile) {
            alert("KTP wajib diunggah.");
            return;
        }

        const formData = new FormData();
        formData.append("customer_name", fullname);
        formData.append("customer_phone", phoneNumber);
        formData.append("customer_email", email);
        formData.append("tower_address_id", towerId);
        formData.append("package_id", productId.toString());

        // 👇 append actual file
        formData.append("id_card_image", idCardFile);

        try {
            const response = await sendRegisterSudirman(formData); // 👈 send FormData
            if (response.status === "success") {
                setModalOpen(true);
            } else {
                alert("Gagal melakukan pendaftaran.");
            }
        } catch (err) {
            console.error("Error submitting data:", err);
            alert("Gagal melakukan pendaftaran.");
        }
    };


    const handleCloseButton = () => {
        navigate('/');
    };

    const handleLogoClick = () => {
        navigate('/');
    };

    const handleCategoryClick = (categorySlug: string, productItem: ProductItem[], category: CategoryProduct) => {
        setCategorySlug(categorySlug);
        setProductItems(productItem);
        setSelectedCategory(category);
        setIsPrice(category.is_price);
    };

    const productClick = (productId: number, price: number) => {
        setProductId(productId);

        setProductPrice(Number(price));

        let deposit = 250000;

        let total = Number(price) + Number(deposit);
        setTotalTransaction(total);
    };

    const handleTowerClick = (regionId: string) => {
        setTowerId(regionId);
    }

    // Handle image upload
    const handleIdCardUpload = (file: File | null) => {
        setIdCardFile(file);
        
        // Create preview URL
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setIdCardPreview(previewUrl);
        } else {
            setIdCardPreview("");
        }
    };

    // Cleanup preview URL on unmount
    useEffect(() => {
        return () => {
            if (idCardPreview) {
                URL.revokeObjectURL(idCardPreview);
            }
        };
    }, [idCardPreview]);

    // Validation state
    const [formErrors, setFormErrors] = useState<{
        fullname?: string;
        email?: string;
        phoneNumber?: string;
        towerId?: string;
        productId?: string;
        privacy?: string;
        idCard?: string;
    }>({});

    // Check if form is valid
    const isFormValid = useMemo(() => {
        // Basic email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        // Validate all required fields
        const isValidEmail = email.trim() !== '' && emailRegex.test(email);
        const isValidPhone = phoneNumber.trim() !== '' && phoneNumber.length >= 9;
        const isValidName = fullname.trim() !== '';
        const isValidTower = categorySlug === "sudirman-park" ? towerId !== '' : true;
        const isValidProduct = productId !== 0;
        const isIdCardUploaded = idCardFile !== null;
        const isPrivacyAccepted = acceptedPrivacy; // Changed this line

        // Update error messages
        const errors: typeof formErrors = {};
        if (!isValidName) errors.fullname = 'Nama lengkap wajib diisi';
        if (!isValidEmail) errors.email = 'Email tidak valid';
        if (!isValidPhone) errors.phoneNumber = 'Nomor telepon tidak valid';
        if (!isValidTower && categorySlug === "sudirman-park") {
            errors.towerId = 'Unit Apartemen wajib dipilih';
        }
        if (!isValidProduct) errors.productId = 'Paket wajib dipilih';
        if (!isIdCardUploaded) errors.idCard = 'Kartu Tanda Penduduk wajib diunggah';
        if (!isPrivacyAccepted) errors.privacy = 'Anda harus menyetujui Kebijakan Privasi'; // Optional: add privacy error

        setFormErrors(errors);

        return (
            isValidEmail &&
            isValidPhone &&
            isValidName &&
            isValidTower &&
            isValidProduct &&
            isIdCardUploaded &&
            isPrivacyAccepted // Changed this line
        );
    }, [fullname, email, phoneNumber, towerId, productId, categorySlug, idCardFile, acceptedPrivacy]);

    // Memoize the change handlers
    const handleFullnameChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setFullname(e.target.value)
    ), []);

    const handleEmailChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)
    ), []);

    const handlePhoneChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)
    ), []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesData = await fetchCategory();
                
                // Filter to get only the category with slug 'sudirman-park'
                const sudirmanParkCategory = categoriesData.filter(element => 
                    element.slug === 'sudirman-park'
                );
                
                // Only set state if the category exists
                if (sudirmanParkCategory.length > 0) {
                    setCategories(sudirmanParkCategory);
                }
            } catch (err) {
                console.error("Failed to fetch category product:", err);
                setError("Failed to load category product. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        setTimeout(() => setLoading(false), 2000);
        fetchCategories();
    }, []);

    useEffect(() => {
        const latitude = -6.2053858;
        const longitude = 106.8173427;
        setUserLocation([latitude, longitude]);
    }, []); // Empty dependency array means this runs once on mount

    if (loading) {
        return (
            <SpinnerLoading/>
        );
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="flex flex-col w-full min-h-screen bg-gray-50">
            <ModalRegister isOpen={isModalOpen} />
            {/* Logo */}
            <img src={logoLM} alt="Logo LM" className="w-32 h-11 md:w-40 md:h-14 mt-6 mx-8 md:mx-40 cursor-pointer" onClick={handleLogoClick} />

            <div className="flex flex-col items-center w-full min-h-screen bg-gray-50">
                {/* Modal Content */}
                <div className="flex flex-col bg-white mx-8 md:w-3/4 md:mx-24 my-6 p-6 rounded-lg shadow-lg">
                    <button 
                        className="text-xl absolute top-4 right-8 md:right-40 md:mx-2 text-gray-600 hover:text-gray-800" 
                        onClick={handleCloseButton}>
                        &times;
                    </button>

                    <h2 className="text-2xl font-semibold text-gray-800 font-poppins">Lengkapi Data Diri Anda</h2>
                    <p className="text-sm text-gray-500 mt-2 font-poppins">
                        Semua data ini wajib di isi untuk kebutuhan proses pemesanan kamu
                    </p>

                    <InputField
                        label="Nama Lengkap"
                        value={fullname}
                        onChange={handleFullnameChange}
                        error={formErrors.fullname}
                        placeholder="Nama lengkap anda"
                    />

                    <div className="mt-4">
                        <label className="text-sm font-medium text-gray-700 font-poppins">Nomor Whatsapp *</label>
                        <div className="flex items-center border rounded-lg mt-1">
                            <span className="px-3 text-gray-700">+62</span>
                            <input
                                type="text"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                placeholder="812 345 678"
                                className={`w-full h-10 px-2 text-sm border-l focus:ring focus:ring-indigo-300 
                                    ${formErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
                            />
                        </div>
                        {formErrors.phoneNumber && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.phoneNumber}</p>
                        )}
                    </div>

                    <InputField
                        label="Email"
                        value={email}
                        onChange={handleEmailChange}
                        error={formErrors.email}
                        placeholder="emailmu@domain.com"
                    />

                    <label className="text-sm text-gray-500 italic mt-2 font-poppins">
                        Pastikan email kamu aktif untuk cek pesanan
                    </label>

                    {/* Add Image Upload for ID Card */}
                    <ImageUpload
                        label="Kartu Tanda Penduduk"
                        onFileSelect={handleIdCardUpload}
                        error={formErrors.idCard}
                        acceptedFormats=".jpg,.jpeg,.png"
                        maxSize={5}
                        previewUrl={idCardPreview}
                    />

                    {/* Tabs Component */}
                    <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Kategori Produk *</label>
                    <TabsComponent onCategoryChange={handleCategoryClick} categories={categories} />

                    {[true].includes(isPrice) && (
                        <>
                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Alamat :</label>
                            <CascadingApartementDropdown onRegionChange={handleTowerClick} />
                            
                            <MapSudirman 
                                center={userLocation ?? defaultCenter} 
                                zoom={13}
                                locationError={locationError}
                            />

                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Paket *</label>
                            <ListProductsRegisterSupark onProductId={productClick} category={selectedCategory} />

                            {/* Total Section */}
                            <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                                <div className="flex justify-between text-sm font-poppins">
                                    <span>Harga Paket</span>
                                    <span>
                                        Rp {new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(productPrice)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm mt-2 text-red-500 line-through font-poppins">
                                    <span>Biaya Instalasi</span>
                                    <span>Rp 250.000</span>
                                </div>
                                <div className="flex justify-between text-sm mt-2 font-poppins">
                                    <span>Deposit</span>
                                    <span>
                                        Rp {new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(250000)}
                                    </span>
                                </div>
                                <hr className="my-2 border-t border-gray-300" />
                                <div className="flex justify-between text-sm font-semibold font-poppins">
                                    <span>Total</span>
                                    <span>Rp {new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalTransaction)}</span>
                                </div>
                            </div>

                            <div className="mt-4 bg-white p-4">
                                <p className="flex justify-between text-sm mt-2 text-red-500 font-poppins">
                                    Syarat & Ketentuan:
                                </p>
                                <p className="flex justify-between text-sm mt-2 text-red-500 font-poppins">
                                    1.⁠ ⁠Harga paket sudah termasuk pajak.
                                </p>
                                <p className="flex justify-between text-sm mt-2 text-red-500 font-poppins">
                                    2.⁠ ⁠Setiap pelanggan wajib membayar deposit Rp 250.000,- untuk setiap 1 (satu) unit Access Point pada saat registrasi.
                                </p>
                                <p className="flex justify-between text-sm mt-2 text-red-500 font-poppins">
                                    3.⁠ ⁠Deposit akan dikembalikan apabila pelanggan berhenti berlangganan dan mengembalikan perangkat Access Point dalam kondisi baik.
                                </p>
                            </div>
                        </>
                    )}

                    <PrivacyCheckboxComponents
                        checked={acceptedPrivacy}
                        onChange={setAcceptedPrivacy}
                    />

                    {/* Submit Button */}
                    <div className="flex justify-end mt-6">
                        <button 
                            onClick={handleButtonClick}
                            disabled={!isFormValid}
                            className={`px-6 py-2 rounded-lg font-semibold font-poppins transition
                                ${isFormValid 
                                    ? 'bg-pink-600 hover:bg-pink-700 text-white cursor-pointer' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                        >
                            Kirim
                        </button>
                    </div>
                </div>
            </div>
            
        </div>
    );
};

export default RegistrationSudirmanPages;